// Hub: post-auth landing. Tile per subscribed product, greyed-out tiles for
// unsubscribed products with a "Contact sales" hint. Single sign-on across
// the platform; the hub doesn't host any product features itself.

import { useEffect, useState } from 'react';
import { useAuth } from './contexts/AuthContext';
import { useOrg } from './contexts/OrgContext';
import { useToast } from './contexts/ToastContext';
import { loadOrgSubscriptions, isActive, type OrgSubscription, type Product } from './services/entitlementService';
import { supabase } from './lib/supabaseClient';
import { AdminPage } from './components/AdminPage';

interface ProductTile {
  product: Product;
  name: string;
  tagline: string;
  emoji: string;
  url: string;
  accent: string;
}

const PRODUCT_TILES: ProductTile[] = [
  {
    product: 'persona_builder',
    name: 'Persona Builder',
    tagline: 'Audience archetypes and message evaluation.',
    emoji: '👥',
    url: import.meta.env.VITE_PERSONA_BUILDER_URL || 'https://personas.krackencode.com',
    accent: 'from-indigo-600/20 to-indigo-900/40 border-indigo-700/40',
  },
  {
    product: 'persona_studio',
    name: 'Persona Studio',
    tagline: 'Build high-fidelity Super Personas from real-world artifacts.',
    emoji: '🎙️',
    url: import.meta.env.VITE_PERSONA_STUDIO_URL || 'https://studio.krackencode.com',
    accent: 'from-amber-600/20 to-amber-900/40 border-amber-700/40',
  },
];

// Platform admin allowlist. Mirrors the server-side check in
// apps/hub/api/_lib/adminAuth.ts (which is the actual security gate); the
// client check just avoids rendering the route to non-admins so it's
// undiscoverable. Anyone not on this list who pastes /admin into the URL
// gets the standard hub view.
const PLATFORM_ADMIN_EMAILS: string[] = ['patrick@ockershauser.com'];

export default function App() {
  const { user, signOut } = useAuth();
  const { currentOrg } = useOrg();
  const { toast } = useToast();
  const [subs, setSubs] = useState<OrgSubscription[]>([]);
  const [loading, setLoading] = useState(true);

  // Cheap pathname routing — only two views (hub vs /admin). React Router
  // would be overkill for this surface. Listen for popstate so back/forward
  // works; admin → hub navigation uses a plain <a href="/"> link.
  const [pathname, setPathname] = useState(() => window.location.pathname);
  useEffect(() => {
    const onPop = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const userEmailLower = (user?.email ?? '').toLowerCase();
  const isPlatformAdmin = PLATFORM_ADMIN_EMAILS.includes(userEmailLower);

  useEffect(() => {
    if (!currentOrg?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await loadOrgSubscriptions(currentOrg.id);
        if (!cancelled) setSubs(list);
      } catch (e) {
        console.error(e);
        toast('Failed to load subscriptions.', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentOrg?.id, toast]);

  // All hooks must run before this branch — an admin toggling between / and
  // /admin otherwise changes the hook count across renders (React error #310).
  if (pathname.startsWith('/admin') && isPlatformAdmin) {
    return <AdminPage />;
  }

  const subByProduct = new Map(subs.map((s) => [s.product, s]));

  return (
    <div className="min-h-screen flex flex-col bg-slate-900 text-slate-200">
      <header className="border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-base font-bold uppercase tracking-widest text-slate-100">Persona Platform</h1>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider">{currentOrg?.name || '(no org)'}</p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          {isPlatformAdmin && (
            <a
              href="/admin"
              className="text-indigo-300 hover:text-indigo-200 uppercase tracking-wider font-bold"
            >
              Admin →
            </a>
          )}
          <span className="text-slate-400">{user?.email}</span>
          <button
            onClick={signOut}
            className="text-slate-400 hover:text-slate-200 uppercase tracking-wider font-bold"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="flex-grow flex items-start justify-center p-8">
        <div className="max-w-4xl w-full space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-slate-100">Welcome back</h2>
            <p className="text-sm text-slate-400 mt-1">Pick an app to get started.</p>
          </div>

          {loading ? (
            <div className="text-xs text-slate-500 animate-pulse uppercase tracking-wider font-bold">
              Loading…
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {PRODUCT_TILES.map((tile) => {
                const sub = subByProduct.get(tile.product);
                const active = isActive(sub);
                return <Tile key={tile.product} tile={tile} active={active} />;
              })}
            </div>
          )}
        </div>
      </main>

      <footer className="border-t border-slate-800 px-6 py-2 text-[10px] text-slate-500 leading-relaxed">
        Persona Platform — single sign-on across all subscribed apps.
      </footer>
    </div>
  );
}

// Hand off the current Supabase session to the destination subdomain via URL
// hash. supabase-js's default detectSessionInUrl behavior parses the same hash
// shape Supabase uses for OAuth callbacks, persists the session, and strips the
// hash before any user-visible render. Without this, localStorage's per-origin
// scope forces a re-auth at every subdomain.
async function openWithSession(tileUrl: string) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    window.location.href = tileUrl;
    return;
  }
  const params = new URLSearchParams({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    expires_in: String(session.expires_in ?? 3600),
    token_type: 'bearer',
    type: 'magiclink',
  });
  window.location.href = `${tileUrl}#${params.toString()}`;
}

function Tile({ tile, active }: { tile: ProductTile; active: boolean }) {
  if (active) {
    return (
      <a
        href={tile.url}
        onClick={(e) => {
          e.preventDefault();
          void openWithSession(tile.url);
        }}
        className={`block rounded-xl border bg-gradient-to-br ${tile.accent} p-6 hover:brightness-125 transition-all`}
      >
        <div className="text-4xl mb-3">{tile.emoji}</div>
        <h3 className="text-lg font-bold text-slate-100">{tile.name}</h3>
        <p className="text-sm text-slate-400 mt-1">{tile.tagline}</p>
        <span className="inline-block mt-4 text-[10px] uppercase tracking-wider font-bold text-indigo-300">
          Open →
        </span>
      </a>
    );
  }
  return (
    <div className="block rounded-xl border border-slate-800 bg-slate-900/60 p-6 opacity-60 cursor-not-allowed">
      <div className="text-4xl mb-3 grayscale">{tile.emoji}</div>
      <h3 className="text-lg font-bold text-slate-300">{tile.name}</h3>
      <p className="text-sm text-slate-500 mt-1">{tile.tagline}</p>
      <span className="inline-block mt-4 text-[10px] uppercase tracking-wider font-bold text-slate-500">
        Not subscribed — contact sales
      </span>
    </div>
  );
}
